import { motion } from "framer-motion";
import { Check, Mail, ShieldAlert, X } from "lucide-react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { LAST_REVIEWED, SCOPE, SECURITY_CONTACT_EMAIL } from "../data/securityDisclosure";

export default function VulnerabilityDisclosurePolicy() {
  useDocumentTitle("Vulnerability Disclosure Policy");
  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={10} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Security
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            Vulnerability Disclosure Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            Found a security issue? Here&apos;s how to report it responsibly, and what to expect from us.
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      <section className="mx-auto max-w-2xl px-6 py-24">
        <ScrollReveal>
          <h2 className="font-display text-xl font-bold text-ocean-950">Our Commitment</h2>
          <p className="mt-3 leading-relaxed text-ocean-800/70">
            We take the security of our systems seriously, and we value the security community&apos;s role in
            keeping ZenFresh and our customers safe. If you believe you&apos;ve found a security vulnerability,
            please report it to us responsibly so we can investigate and fix it.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.08} className="mt-10">
          <h2 className="font-display text-xl font-bold text-ocean-950">Scope</h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed text-ocean-800/70">
            {SCOPE.map((item) => (
              <li key={item.label} className="flex items-start gap-2">
                {item.inScope ? (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
                ) : (
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-coral-500" />
                )}
                {item.label}
              </li>
            ))}
          </ul>
        </ScrollReveal>

        <ScrollReveal delay={0.16} className="mt-10">
          <h2 className="font-display text-xl font-bold text-ocean-950">What to Expect</h2>
          <p className="mt-3 leading-relaxed text-ocean-800/70">
            We aim to acknowledge new reports within <strong>3 business days</strong> and provide an initial
            assessment within <strong>10 business days</strong>. We&apos;ll keep you updated as we work on a
            fix, and we&apos;ll let you know once it&apos;s resolved.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.24} className="mt-10 rounded-2xl bg-teal-400/10 p-6">
          <div className="flex items-center gap-2 text-teal-700">
            <ShieldAlert className="h-5 w-5" />
            <h2 className="font-display text-lg font-bold">Safe Harbour</h2>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-ocean-800/70">
            We will not pursue legal action against researchers who discover and report vulnerabilities in
            good faith, in accordance with this policy — provided you avoid privacy violations, service
            disruption, and give us a reasonable time to investigate and remediate before any public
            disclosure.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.32} className="mt-10 rounded-2xl bg-foam-100 p-6">
          <h2 className="font-display text-lg font-bold text-ocean-950">Report an Issue</h2>
          <p className="mt-2 text-sm leading-relaxed text-ocean-800/70">
            Reports go directly to our engineering/security team, separate from general support — please
            include enough detail (steps to reproduce, affected URL, potential impact) for us to triage
            quickly.
          </p>
          <a
            href={`mailto:${SECURITY_CONTACT_EMAIL}`}
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-teal-600 hover:text-teal-700"
          >
            <Mail className="h-4 w-4" />
            {SECURITY_CONTACT_EMAIL}
          </a>
        </ScrollReveal>

        <ScrollReveal delay={0.4} className="mt-10 text-xs uppercase tracking-wide text-ocean-800/40">
          Last reviewed: {LAST_REVIEWED}
        </ScrollReveal>
      </section>
    </PageTransition>
  );
}
